import { ActivityIndicator, Text, View } from 'react-native'
import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react-native'
import { Minus, TrendingDown, TrendingUp } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { Card } from '../../shared/Card'

interface StatCardProps {
  label: string
  value: number
  delta: number
  comparisonLabel: string
  color: string
  icon: ComponentType<LucideProps>
  loading?: boolean
}

function DeltaChip({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <View className="flex-row items-center gap-1">
        <Minus size={11} color={colors.text.muted} strokeWidth={2.5} />
        <Text className="text-xs font-semibold text-muted">0</Text>
      </View>
    )
  }
  const isPositive = delta > 0
  return (
    <View className="flex-row items-center gap-1">
      {isPositive
        ? <TrendingUp size={11} color={colors.success} strokeWidth={2.5} />
        : <TrendingDown size={11} color={colors.accent.DEFAULT} strokeWidth={2.5} />
      }
      <Text className={`text-xs font-semibold ${isPositive ? 'text-success' : 'text-accent'}`}>
        {Math.abs(delta)}
      </Text>
    </View>
  )
}

export function StatCard({ label, value, delta, comparisonLabel, color, icon: Icon, loading }: StatCardProps) {
  return (
    <Card variant="dashboard" className="flex-1 p-4 lg:p-5">
      <View className="flex-row items-center gap-3">
        <View
          className="h-12 w-12 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${color}1A` }}
        >
          <Icon size={24} color={color} strokeWidth={2} />
        </View>
        {loading
          ? <ActivityIndicator size="small" color={colors.text.muted} />
          : <Text className="text-3xl font-bold text-primary">{value.toLocaleString()}</Text>
        }
      </View>
      <View className="mt-2 flex-row items-center justify-between">
        <Text className="text-sm font-medium text-secondary">{label}</Text>
        {!loading && <DeltaChip delta={delta} />}
      </View>
      <Text className="mt-0.5 text-xs font-normal text-muted">{comparisonLabel}</Text>
    </Card>
  )
}